/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { useNavigate } from 'react-router-dom';
import { MOASHELF_PATH } from '@constant/moashelf/route-path';
import IconTextBlock from 'component/common/block/icon-text-block';
import TextBlock from 'component/common/block/text-block';
import Gap from '@component/common/gap';
import SideContainer from '@component/common/architecture/side-container';
import { v4 as uuid4 } from 'uuid';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import otterLogoImage from '@asset/image/otter-brick-logo.webp';
import ImageTextBlock from 'component/common/block/image-text-block';
import { MS_BEGIN_MENUS, MS_SHELF_MENUS, SUBSCRIPTION_MENUS } from '@constant/variable';
import ModalBackground from '@component/common/modal/modal-background';
import { IMoashelfMenu } from '@datatype/moashelf/type';
import { createAuthLogoText } from '@util/string';
import { AUTH_PATH } from '@constant/auth/route-path';

const MenuList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TermContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: start;
  padding: 0 0 0 ${size.MARGIN_2};
  width: 100%;
  position: absolute;
  bottom: 30px;
`;

const LOGO_DESIGN = {
  box: {
    gap: size.MARGIN_1,
    width: '100%',
    alignX: 'left',
    margin: `${size.MARGIN_2} 0px ${size.MARGIN_2} 0px`,
    padding: `0px 0px 0px ${size.MARGIN_2}`,
  },
  image: { height: '60px' },
  text: { size: 'medium', color: color.WHITE_1, bold: true },
} as const;

const SideTab: React.FC<any> = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const handleMainMenuClick = (mainMenu: IMoashelfMenu['main']) => {
    switch (mainMenu) {
      case 'shelf':
        dispatch(navigationActions.storeMoashelfMainMenu('shelf'));
        navigate(MOASHELF_PATH.SHELF_PATH);
        break;
      case 'subscription':
        dispatch(navigationActions.storeMoashelfMainMenu('subscription'));
        navigate(MOASHELF_PATH.SUBSCRIPTION_PATH);
        break;
      default:
    }
  };

  const handleTermClick = () => navigate(AUTH_PATH.TERM_PATH);
  const handleServiceTermClick = () => navigate(AUTH_PATH.SERVICE_TERM_PATH);
  const handlePrivateTermClick = () => navigate(AUTH_PATH.PRIVATE_TERM_PATH);
  const handleMobileModalClick = () => dispatch(navigationActions.closeMobileSideTab());

  const clickedMainMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.moashelf?.mainMenu) ?? null;
  const clickedSubMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.moashelf?.subMenu) ?? null;
  const isMobileSideTab = useTypedSelector((state) => state.rootReducer?.navigationReducer?.isMobileSideTab) ?? false;

  const createMainMenuDesign = (mainMenu: IMoashelfMenu['main'], clickedMainMenu, device: 'web' | 'mobile') => {
    const isClicked = clickedMainMenu === mainMenu;

    return {
      text: { size: 'medium', color: color.WHITE_1 },
      icon: { size: 'medium', kind: 'outlined', color: color.WHITE_1 },
      box: {
        gap: '10px',
        width: '100%',
        height: '35px',
        alignX: 'left',
        padding: `0 0 0 ${size.MARGIN_2}`,
        backgroundColor: isClicked ? color.BLACK_1 : 'transparent',
        event: {
          cursor: 'pointer',
          hover: `
            background-color: ${color.BLACK_1};
            border: none;
          `,
        },
      },
    } as const;
  };

  const createSubMenuItem = (item: any, device: 'web' | 'mobile') => {
    const { type, menu, text, icon } = item;

    const handleBeginClick = (beginMenu: IMoashelfMenu['begin']) => {
      dispatch(navigationActions.storeMoashelfSubMenu(beginMenu));
      navigate(MOASHELF_PATH.BEGIN_PATH);
      if (IS_IYAGIGO_MOBILE_VIEW) dispatch(navigationActions.closeMobileSideTab());
    };

    const handleShelfClick = (shelfMenu: IMoashelfMenu['shelf']) => {
      dispatch(navigationActions.storeMoashelfSubMenu(shelfMenu));
      navigate(MOASHELF_PATH.SHELF_PATH);
      if (IS_IYAGIGO_MOBILE_VIEW) dispatch(navigationActions.closeMobileSideTab());
    };

    const handleSubscriptionClick = (subscriptionMenu: IMoashelfMenu['subscription']) => {
      dispatch(navigationActions.storeMoashelfSubMenu(subscriptionMenu));
      navigate(MOASHELF_PATH.SUBSCRIPTION_PATH);
      if (IS_IYAGIGO_MOBILE_VIEW) dispatch(navigationActions.closeMobileSideTab());
    };

    const handleClick = () => {
      if (type === 'begin') {
        handleBeginClick(menu);
      } else if (type === 'shelf') {
        handleShelfClick(menu);
      } else if (type === 'subscription') {
        handleSubscriptionClick(menu);
      }
    };

    const createSubMenuDesign = (
      subMenu: IMoashelfMenu['sub'],
      clickedSubMenu: IMoashelfMenu['sub'],
      device: 'web' | 'mobile',
    ) => {
      const isClicked = clickedSubMenu === subMenu;

      if (device === 'web') {
        return {
          text: {
            size: 'medium',
            ...(isClicked ? { color: color.WHITE_1, bold: true } : { color: color.GRAY_2 }),
            event: {
              cursor: 'pointer',
              hover: `
              border: none;
              color: ${color.WHITE_1};
            `,
            },
          },
          box: {
            width: '100%',
            height: '35px',
            alignX: 'left',
            padding: `0 0 0 ${size.MARGIN_4}`,
            event: { cursor: 'pointer' },
          },
        } as const;
      } else if (device === 'mobile') {
        return {
          icon: { size: 'medium', kind: 'outlined', color: color.WHITE_1 },
          text: { size: 'medium', color: color.WHITE_1 },
          box: {
            gap: '10px',
            width: '100%',
            height: '35px',
            alignX: 'left',
            padding: `0 0 0 ${size.MARGIN_2}`,
            backgroundColor: isClicked ? color.BLACK_1 : 'transparent',
          },
        } as const;
      } else {
        return {};
      }
    };

    return device === 'web' ? (
      <TextBlock
        key={uuid4()}
        text={text}
        design={createSubMenuDesign(menu, clickedSubMenu, device)}
        click={{ handler: handleClick }}
      />
    ) : (
      <IconTextBlock
        key={uuid4()}
        text={text}
        design={createSubMenuDesign(menu, clickedSubMenu, device)}
        click={{ handler: handleClick }}
        icon={icon?.icon}
      />
    );
  };

  const WebBeginMenus = MS_BEGIN_MENUS.map((item) => createSubMenuItem(item, 'web'));
  const WebShelfMenus = MS_SHELF_MENUS.map((item) => createSubMenuItem(item, 'web'));
  const WebSubscriptionMenus = SUBSCRIPTION_MENUS.map((item) => createSubMenuItem(item, 'web'));
  const MobileBeginMenus = MS_BEGIN_MENUS.map((item) => createSubMenuItem(item, 'mobile'));
  const MobileShelfMenus = MS_SHELF_MENUS.map((item) => createSubMenuItem(item, 'mobile'));
  const MobileSubscriptionMenus = SUBSCRIPTION_MENUS.map((item) => createSubMenuItem(item, 'mobile'));

  return IS_IYAGIGO_MOBILE_VIEW && isMobileSideTab ? (
    <ModalBackground onClick={handleMobileModalClick}>
      <SideContainer
        design={{
          width: size.MOBILE_SIDE_WIDTH,
          backgroundColor: color.BLACK_2,
          top: '0px',
          align: 'up',
        }}>
        <ImageTextBlock src={otterLogoImage} alt='otterbrick' text={createAuthLogoText()} design={LOGO_DESIGN} />
        {clickedMainMenu === 'begin' && <MenuList>{MobileBeginMenus}</MenuList>}
        {clickedMainMenu === 'shelf' && <MenuList>{MobileShelfMenus}</MenuList>}
        {clickedMainMenu === 'subscription' && <MenuList>{MobileSubscriptionMenus}</MenuList>}
        <TermContainer>
          <TextBlock
            text='서비스이용약관'
            design={{
              text: { color: color.GRAY_2, size: 'medium' },
              box: {
                width: '100%',
                alignX: 'left',
                event: { cursor: 'pointer' },
              },
            }}
            click={{ handler: handleServiceTermClick }}
          />
          <Gap height={'10px'} />
          <TextBlock
            text='개인정보처리방침'
            design={{
              text: { color: color.GRAY_2, size: 'medium' },
              box: {
                width: '100%',
                alignX: 'left',
                event: { cursor: 'pointer' },
              },
            }}
            click={{ handler: handlePrivateTermClick }}
          />
        </TermContainer>
      </SideContainer>
    </ModalBackground>
  ) : (
    <SideContainer
      design={{
        width: size.WEB_SIDE_WIDTH,
        backgroundColor: color.BLACK_2,
        top: '0px',
        align: 'up',
      }}>
      <ImageTextBlock src={otterLogoImage} alt='otterbrick' text={createAuthLogoText()} design={LOGO_DESIGN} />
      <MenuList>
        <IconTextBlock
          icon={'window'}
          text='시작하기'
          design={createMainMenuDesign('begin', clickedMainMenu, 'web')}
          click={{ handler: () => handleMainMenuClick('begin') }}
        />
        <MenuList>{WebBeginMenus}</MenuList>
        <Gap height={size.MARGIN_2} />
        <IconTextBlock
          icon={'mail'}
          text='읽을거리'
          design={createMainMenuDesign('shelf', clickedMainMenu, 'web')}
          click={{ handler: () => handleMainMenuClick('shelf') }}
        />
        <MenuList>{WebShelfMenus}</MenuList>
        <Gap height={size.MARGIN_2} />
        <IconTextBlock
          icon={'inventory'}
          text='구독관리'
          design={createMainMenuDesign('subscription', clickedMainMenu, 'web')}
          click={{ handler: () => handleMainMenuClick('subscription') }}
        />
        <MenuList>{WebSubscriptionMenus}</MenuList>
        <TermContainer>
          <TextBlock
            text='서비스이용약관'
            design={{
              text: { color: color.GRAY_2, size: 'medium' },
              box: {
                width: '100%',
                alignX: 'left',
                event: { cursor: 'pointer' },
              },
            }}
            click={{ handler: handleServiceTermClick }}
          />
          <Gap height={'10px'} />
          <TextBlock
            text='개인정보처리방침'
            design={{
              text: { color: color.GRAY_2, size: 'medium' },
              box: {
                width: '100%',
                alignX: 'left',
                event: { cursor: 'pointer' },
              },
            }}
            click={{ handler: handlePrivateTermClick }}
          />
        </TermContainer>

        {/*<Gap height={size.MARGIN_2} />*/}
        {/*<IconTextBlock icon={'help'} text='도움말' design={getWebMainMenuDesign('subscription', clickedMainMenu)} />*/}
        {/*<Gap height={size.MARGIN_2} />*/}
        {/*<IconTextBlock icon={'send'} text='의견보내기' design={getWebMainMenuDesign('subscription', clickedMainMenu)} />*/}
      </MenuList>
    </SideContainer>
  );
};

// TODO: 경제, 통계, 책, Q&A, 전체보기

export default SideTab;
